import Vue from 'vue'
import * as decimalUtils from '@/utils/decimalUtils'

function toAbsoluteNumber (value) {
  return Math.abs(Number(value || 0))
}

function sumAllocationAmountsAbs (entries) {
  return decimalUtils.sumReduce((entries || []).map(function (entry) {
    return toAbsoluteNumber(entry && entry.Amount)
  }))
}

function hasNumericValue (value) {
  return value !== null && value !== undefined && !isNaN(Number(value))
}

function clampToZero (value) {
  return value < 0 ? 0 : value
}

function getTargetRemainingBase (state) {
  if (!state.transaction) return 0
  var transaction = state.transaction

  if (hasNumericValue(transaction.Value) && hasNumericValue(transaction.Allocated)) {
    return clampToZero(decimalUtils.subtract(
      toAbsoluteNumber(transaction.Value),
      toAbsoluteNumber(transaction.Allocated)
    ))
  }

  if (hasNumericValue(transaction.RemainingBalance)) {
    return toAbsoluteNumber(transaction.RemainingBalance)
  }

  return 0
}

function remainingCapacity (remainingBalance) {
  if (remainingBalance === null || remainingBalance === undefined) return null
  return Math.abs(Number(remainingBalance))
}

function getDefaultFilters () {
  return {
    reference: '',
    date: null,
    amountMin: null,
    amountMax: null,
    remainingMin: null,
    remainingMax: null
  }
}

function cloneFilters (filters) {
  var source = filters || {}
  return {
    reference: source.reference || '',
    date: source.date || null,
    amountMin: source.amountMin !== undefined ? source.amountMin : null,
    amountMax: source.amountMax !== undefined ? source.amountMax : null,
    remainingMin: source.remainingMin !== undefined ? source.remainingMin : null,
    remainingMax: source.remainingMax !== undefined ? source.remainingMax : null
  }
}

function normalizeCustomerReferenceKey (customerReference) {
  if (customerReference === null || customerReference === undefined) return null
  var normalized = String(customerReference).trim().toLowerCase()
  return normalized || null
}

function resolveCustomerReference (context, payload, transaction) {
  var fromTransaction = transaction && (
    transaction.CustomerReference ||
    transaction.customerReference ||
    transaction.CustomerId ||
    transaction.customerId
  )
  if (fromTransaction !== null && fromTransaction !== undefined && fromTransaction !== '') {
    return String(fromTransaction)
  }

  var fromPayload = payload && (payload.customerReference || payload.customerId)
  if (fromPayload !== null && fromPayload !== undefined && fromPayload !== '') {
    return String(fromPayload)
  }

  var fromRoot = context.rootState &&
    context.rootState.customers &&
    context.rootState.customers.customer &&
    context.rootState.customers.customer.CustomerReference
  if (fromRoot !== null && fromRoot !== undefined && fromRoot !== '') {
    return String(fromRoot)
  }

  return null
}

export var state = {
  transaction: null,
  availableTypes: [],
  allocations: [],
  pendingAllocations: [],
  tabData: {},
  tabPage: {},
  tabSort: {},
  activeTab: null,
  filters: getDefaultFilters(),
  currentCustomerReferenceKey: null,
  filtersByCustomerReference: {},
  selectedAvailableId: null,
  selectedAllocationId: null,
  allocateAmount: null,
  loading: false,
  tabLoading: false,
  saving: false,
  undoing: false,
  error: null,
  fatalError: null
}

export var mutations = {
  setTransaction: function (state, transaction) {
    state.transaction = transaction
  },
  setAvailableTypes: function (state, availableTypes) {
    state.availableTypes = availableTypes
  },
  setAllocations: function (state, allocations) {
    state.allocations = allocations || []
  },
  setActiveTab: function (state, tab) {
    state.activeTab = tab
  },
  setLoading: function (state, loading) {
    state.loading = loading
  },
  setTabLoading: function (state, tabLoading) {
    state.tabLoading = tabLoading
  },
  setSaving: function (state, saving) {
    state.saving = saving
  },
  setUndoing: function (state, undoing) {
    state.undoing = undoing
  },
  setError: function (state, error) {
    state.error = error
  },
  setFatalError: function (state, fatalError) {
    state.fatalError = fatalError
  },
  setAllocateAmount: function (state, amount) {
    state.allocateAmount = amount
  },
  setSelectedAvailableId: function (state, id) {
    state.selectedAvailableId = id
  },
  setSelectedAllocationId: function (state, id) {
    state.selectedAllocationId = id
  },
  setTabData: function (state, payload) {
    // Use Vue.set to ensure reactivity when adding new key to tabData object
    Vue.set(state.tabData, payload.type, payload.data)
  },
  clearTabData: function (state) {
    state.tabData = {}
  },
  setTabPage: function (state, payload) {
    Vue.set(state.tabPage, payload.type, payload.page)
  },
  clearTabPage: function (state) {
    state.tabPage = {}
  },
  setTabSort: function (state, payload) {
    Vue.set(state.tabSort, payload.type, {
      sortBy: payload.sortBy,
      sortDirection: payload.sortDirection
    })
  },
  clearTabSort: function (state) {
    state.tabSort = {}
  },
  setCustomerFilterContext: function (state, customerReference) {
    var key = normalizeCustomerReferenceKey(customerReference)
    state.currentCustomerReferenceKey = key
    if (key && state.filtersByCustomerReference && state.filtersByCustomerReference[key]) {
      state.filters = cloneFilters(state.filtersByCustomerReference[key])
      return
    }
    state.filters = getDefaultFilters()
  },
  setFilters: function (state, filters) {
    var normalizedFilters = cloneFilters(filters)
    state.filters = normalizedFilters
    if (state.currentCustomerReferenceKey) {
      Vue.set(state.filtersByCustomerReference, state.currentCustomerReferenceKey, cloneFilters(normalizedFilters))
    }
  },
  resetFilters: function (state) {
    var defaultFilters = getDefaultFilters()
    state.filters = defaultFilters
    if (state.currentCustomerReferenceKey) {
      Vue.set(state.filtersByCustomerReference, state.currentCustomerReferenceKey, cloneFilters(defaultFilters))
    }
  },
  addPendingAllocation: function (state, entry) {
    state.pendingAllocations = state.pendingAllocations.concat([entry])
    state.allocateAmount = null
    state.selectedAvailableId = null
    state.selectedAllocationId = null
  },
  updatePendingAllocation: function (state, payload) {
    if (payload.Amount === 0) {
      state.pendingAllocations = state.pendingAllocations.filter(function (p) {
        return p.FromTransactionId !== payload.FromTransactionId
      })
    } else {
      state.pendingAllocations = state.pendingAllocations.map(function (p) {
        if (p.FromTransactionId === payload.FromTransactionId) {
          return Object.assign({}, p, { Amount: payload.Amount })
        }
        return p
      })
    }
  },
  removePendingAllocation: function (state, fromTransactionId) {
    state.pendingAllocations = state.pendingAllocations.filter(function (p) {
      return p.FromTransactionId !== fromTransactionId
    })
  },
  clearSelection: function (state) {
    state.selectedAvailableId = null
    state.selectedAllocationId = null
    state.allocateAmount = null
  },
  setPendingAllocations: function (state, pendingAllocations) {
    state.pendingAllocations = pendingAllocations
  },
  removeAllocationById: function (state, allocationId) {
    state.allocations = state.allocations.filter(function (allocation) {
      return allocation.Id !== allocationId
    })
    if (state.activeTab) {
      var hasActiveTabRows = state.allocations.some(function (allocation) {
        return allocation.FromType === state.activeTab
      })
      if (!hasActiveTabRows) {
        var next = state.allocations.find(function (allocation) { return !!allocation.FromType })
        state.activeTab = next ? next.FromType : null
      }
    }
  }
}

export var actions = {
  fetchAllocationDialogViewModel: function (context, payload) {
    var transactionId = payload.transactionId
    var transactionType = payload.transactionType
    context.commit('setLoading', true)
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain + '/api/v1/viewmodels/allocation-dialog/' + transactionId + '?transactionType=' + transactionType
    return Vue.prototype.$http.get(url, { withCredentials: true })
      .then(function (response) {
        var transaction = response.data && (response.data.Transaction || response.data.transaction)
        var availableTypes = response.data && (response.data.AvailableTypes || response.data.availableTypes)
        var allocations = response.data && (response.data.Allocations || response.data.allocations)
        var pendingAllocations = response.data && (response.data.PendingAllocations || response.data.pendingAllocations)
        var customerReference = resolveCustomerReference(context, payload, transaction)
        context.commit('setTransaction', transaction || null)
        context.commit('setAvailableTypes', availableTypes || [])
        context.commit('setAllocations', allocations || [])
        context.commit('setPendingAllocations', pendingAllocations || [])
        context.commit('setCustomerFilterContext', customerReference)
        context.commit('setLoading', false)
        var types = availableTypes || []
        if (types && types.length > 0) {
          context.commit('setActiveTab', types[0])
          context.dispatch('fetchTabTransactions', { transactionId: transactionId, transactionType: types[0] })
        }
      })
      .catch(function () {
        context.commit('setFatalError', 'Unable to load allocation data. Please try again or contact support.')
        context.commit('setLoading', false)
      })
  },

  fetchEditAllocations: function (context, payload) {
    var transactionId = payload.transactionId
    var transactionType = payload.transactionType
    context.commit('setLoading', true)
    context.commit('setFatalError', null)
    context.commit('setError', null)
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain + '/api/v1/viewmodels/allocation-dialog/' + transactionId + '?transactionType=' + transactionType
    return Vue.prototype.$http.get(url, { withCredentials: true })
      .then(function (response) {
        var transaction = response.data && response.data.Transaction ? response.data.Transaction : null
        var allocations = response.data && response.data.Allocations ? response.data.Allocations : []
        var customerReference = resolveCustomerReference(context, payload, transaction)
        context.commit('setTransaction', transaction)
        context.commit('setAllocations', allocations)
        context.commit('setCustomerFilterContext', customerReference)
        var firstTab = null
        var i
        for (i = 0; i < allocations.length; i++) {
          if (allocations[i] && allocations[i].FromType) {
            firstTab = allocations[i].FromType
            break
          }
        }
        context.commit('setActiveTab', firstTab)
        context.commit('setLoading', false)
      })
      .catch(function () {
        context.commit('setFatalError', 'Unable to load allocation data. Please try again or contact support.')
        context.commit('setLoading', false)
      })
  },

  undoAllocation: function (context, payload) {
    var allocationId = payload && payload.allocationId
    var customerReference = payload && payload.customerReference
    context.commit('setUndoing', true)
    context.commit('setError', null)

    var existingCustomerReference = context.rootState &&
      context.rootState.customers &&
      context.rootState.customers.customer &&
      context.rootState.customers.customer.CustomerReference

    var shouldRefreshCustomer = !!customerReference && (
      !existingCustomerReference ||
      String(existingCustomerReference).toLowerCase() !== String(customerReference).toLowerCase()
    )

    var refreshPromise = Promise.resolve()
    if (shouldRefreshCustomer) {
      refreshPromise = Promise.resolve(context.dispatch('setCustomer', customerReference, { root: true }))
    }

    return refreshPromise
      .then(function () {
        var accountId = context.rootState &&
          context.rootState.customers &&
          context.rootState.customers.customer &&
          context.rootState.customers.customer.AccountId

        if (accountId === null || accountId === undefined) {
          context.commit('setError', 'Unable to undo allocation. Account context could not be resolved.')
          context.commit('setUndoing', false)
          return Promise.resolve()
        }

        var url = 'https://' + Vue.config.honeycomb.moneybot.domain + '/api/v1/allocations/undo/' + accountId + '/' + allocationId
        return Vue.prototype.$http.post(url, null, { withCredentials: true })
          .then(function () {
            context.commit('removeAllocationById', allocationId)
            context.commit('setUndoing', false)
          })
          .catch(function (error) {
            var status = error && error.response && error.response.status
            if (status === 400) {
              context.commit('setError', 'Unable to undo allocation. Invalid request.')
            } else if (status === 401) {
              context.commit('setError', 'You are not authorised to perform this action.')
            } else if (status === 403) {
              context.commit('setError', 'You do not have permission to undo allocations.')
            } else if (status === 404) {
              context.commit('setError', 'Allocation not found. It may have already been removed.')
            } else {
              context.commit('setError', 'An unexpected error occurred. Please try again.')
            }
            context.commit('setUndoing', false)
          })
      })
      .catch(function () {
        context.commit('setError', 'Unable to resolve customer context for this action.')
        context.commit('setUndoing', false)
      })
  },

  fetchTabTransactions: function (context, payload) {
    var transactionId = payload.transactionId
    var transactionType = payload.transactionType
    // Cache hit — return immediately without making an HTTP call
    if (context.state.tabData[transactionType]) {
      return
    }
    context.commit('setTabLoading', true)
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain +
      '/api/v1/viewmodels/allocation-dialog/' + transactionId + '/transactions/' + transactionType +
      '?bankOrSales=' + context.state.transaction.BankOrSales
    var body = context.getters.tabRequestBody
    return Vue.prototype.$http.post(url, body, { withCredentials: true })
      .then(function (response) {
        context.commit('setTabData', {
          type: transactionType,
          data: { Transactions: response.data.Transactions, Page: response.data.Page }
        })
        context.commit('setTabLoading', false)
      })
      .catch(function () {
        context.commit('setError', 'Failed to load transactions. Please try again.')
        context.commit('setTabLoading', false)
      })
  },

  submitAllocations: function (context, transactionId) {
    context.commit('setSaving', true)
    var transactionAccountId = context.state.transaction && context.state.transaction.AccountId
    var customerAccountId = context.rootState &&
      context.rootState.customers &&
      context.rootState.customers.customer &&
      context.rootState.customers.customer.AccountId
    var accountId = transactionAccountId !== null && transactionAccountId !== undefined
      ? transactionAccountId
      : customerAccountId
    if (accountId === null || accountId === undefined) {
      context.commit('setError', 'Unable to save allocations. AccountId is required.')
      context.commit('setSaving', false)
      return Promise.resolve()
    }
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain + '/api/v1/viewmodels/allocation-dialog?accountId=' + accountId
    var body = {
      transaction: context.state.transaction,
      availableTypes: context.state.availableTypes,
      allocations: context.state.allocations,
      pendingAllocations: context.state.pendingAllocations
    }
    var priorPending = context.state.pendingAllocations.slice()
    return Vue.prototype.$http.post(url, body, { withCredentials: true })
      .then(function () {
        context.dispatch('fetchAllocationDialogViewModel', { transactionId: transactionId, transactionType: context.state.transaction.BankOrSales })
        context.commit('setPendingAllocations', [])
        context.commit('clearTabData')
        context.commit('setSaving', false)
      })
      .catch(function (error) {
        var status = error.response && error.response.status
        if (status === 409) {
          context.dispatch('fetchAllocationDialogViewModel', { transactionId: transactionId, transactionType: context.state.transaction.BankOrSales })
          context.commit('setError', 'Some allocations could not be saved because balances have changed. Please review and adjust the highlighted entries.')
        } else if (status === 400) {
          var msg = (error.response.data && error.response.data.message) || 'Validation failed. Please check your allocations and try again.'
          context.commit('setError', msg)
        } else if (status === 503) {
          context.commit('setError', 'Service temporarily unavailable. Please try again in a moment.')
        } else {
          context.commit('setError', 'An unexpected error occurred. Please try again.')
        }
        context.commit('setSaving', false)
        // Reattach prior pending on 409 for user correction
        if (status === 409 && priorPending.length > 0) {
          context.commit('setPendingAllocations', priorPending)
        }
      })
  }
}

export var getters = {
  liveTargetRemaining: function (state) {
    if (!state.transaction) return 0
    var pendingSum = sumAllocationAmountsAbs(state.pendingAllocations)
    return clampToZero(decimalUtils.subtract(getTargetRemainingBase(state), pendingSum))
  },

  liveSourceRemaining: function (state) {
    return function (itemId) {
      var tab = state.activeTab && state.tabData[state.activeTab]
      if (!tab) return 0
      var item = tab.Transactions && tab.Transactions.find(function (t) { return t.Id === itemId })
      if (!item) return 0
      // Return null when RemainingBalance is unknown so callers can distinguish
      // "unknown balance" from "zero balance" (null is treated as "show the item").
      if (item.RemainingBalance === null || item.RemainingBalance === undefined) return null
      var pendingSum = sumAllocationAmountsAbs(
        state.pendingAllocations
          .filter(function (p) {
            return p.FromTransactionId === itemId && itemId !== state.selectedAllocationId
          })
      )
      return clampToZero(decimalUtils.subtract(toAbsoluteNumber(item.RemainingBalance), pendingSum))
    }
  },

  isEditMode: function (state) {
    if (state.selectedAllocationId === null) return false
    return state.pendingAllocations.some(function (p) {
      return p.FromTransactionId === state.selectedAllocationId
    })
  },

  currentAllocationAmount: function (state) {
    if (state.selectedAllocationId === null) return 0
    var entry = state.pendingAllocations.find(function (p) {
      return p.FromTransactionId === state.selectedAllocationId
    })
    return entry ? entry.Amount : 0
  },

  availableItems: function (state, getters) {
    if (!state.activeTab || !state.tabData[state.activeTab]) return []
    var transactions = state.tabData[state.activeTab].Transactions || []
    return transactions.filter(function (t) {
      var remaining = getters.liveSourceRemaining(t.Id)
      // null = unknown balance (API did not return RemainingBalance) — show the item
      // any non-zero magnitude = has remaining balance — show the item
      // zero = fully allocated — hide the item
      return remaining === null || Math.abs(Number(remaining)) > 0
    })
  },

  maxAllocateAmount: function (state, getters) {
    var sourceId = state.selectedAvailableId
    var sourceCapacity = remainingCapacity(getters.liveSourceRemaining(sourceId))
    var targetCapacity = remainingCapacity(getters.liveTargetRemaining)
    if (sourceCapacity === null || targetCapacity === null) return 0
    if (getters.isEditMode) {
      var current = getters.currentAllocationAmount
      return decimalUtils.min(
        sourceCapacity + current,
        targetCapacity + current
      )
    }
    return decimalUtils.min(
      sourceCapacity,
      targetCapacity
    )
  },

  allocationError: function (state, getters) {
    return function (allocateAmount) {
      if (state.selectedAvailableId === null && state.selectedAllocationId === null) return null
      var amount = Number(allocateAmount)
      if (isNaN(amount) || amount <= 0) {
        return 'Please enter a valid amount greater than zero.'
      }
      var sourceId = getters.isEditMode ? state.selectedAllocationId : state.selectedAvailableId
      var sourceCapacity = remainingCapacity(getters.liveSourceRemaining(sourceId))
      if (sourceCapacity !== null && amount > sourceCapacity) {
        return 'Amount exceeds remaining balance of the source transaction.'
      }
      var targetCapacity = remainingCapacity(getters.liveTargetRemaining)
      if (targetCapacity !== null && amount > targetCapacity) {
        return 'Amount exceeds remaining balance of the target transaction.'
      }
      return null
    }
  },

  isAllocateDisabled: function (state, getters) {
    return function (allocateAmount) {
      if (state.selectedAvailableId === null && state.selectedAllocationId === null) return true
      return getters.allocationError(allocateAmount) !== null
    }
  },

  tabRequestBody: function (state) {
    var currentPage = (state.tabPage && state.activeTab && state.tabPage[state.activeTab]) || 1
    var defaultSort = { sortBy: 'Value', sortDirection: 'asc' }
    var currentSort = (state.tabSort && state.activeTab && state.tabSort[state.activeTab]) || defaultSort
    return {
      pageIndex: currentPage - 1,
      pageSize: 10,
      sortBy: currentSort.sortBy || defaultSort.sortBy,
      sortDirection: currentSort.sortDirection || defaultSort.sortDirection,
      reference: state.filters.reference || null,
      valueFrom: state.filters.amountMin !== null ? state.filters.amountMin : null,
      valueTo: state.filters.amountMax !== null ? state.filters.amountMax : null,
      balanceFrom: state.filters.remainingMin !== null ? state.filters.remainingMin : null,
      balanceTo: state.filters.remainingMax !== null ? state.filters.remainingMax : null
    }
  },

  tabCurrentPage: function (state) {
    return (state.tabPage && state.activeTab && state.tabPage[state.activeTab]) || 1
  },

  tabTotalCount: function (state) {
    var tab = state.tabData && state.activeTab && state.tabData[state.activeTab]
    return (tab && tab.Page && tab.Page.TotalCount) || 0
  },

  tabPageSize: function (state) {
    var tab = state.tabData && state.activeTab && state.tabData[state.activeTab]
    return (tab && tab.Page && tab.Page.Size) || 10
  },

  editAvailableTabs: function (state) {
    var tabs = []
    var i
    for (i = 0; i < state.allocations.length; i++) {
      var type = state.allocations[i] && state.allocations[i].FromType
      if (type && tabs.indexOf(type) === -1) {
        tabs.push(type)
      }
    }
    return tabs
  },

  filteredAllocations: function (state) {
    var rows = state.allocations.slice()
    if (state.activeTab) {
      rows = rows.filter(function (allocation) {
        return allocation.FromType === state.activeTab
      })
    }

    if (state.filters.reference) {
      var referenceSearch = String(state.filters.reference).toLowerCase()
      rows = rows.filter(function (allocation) {
        return allocation.FromReference && String(allocation.FromReference).toLowerCase().indexOf(referenceSearch) !== -1
      })
    }

    if (state.filters.date) {
      var filterDate = new Date(state.filters.date)
      var filterKey = filterDate.getFullYear() + '-' + filterDate.getMonth() + '-' + filterDate.getDate()
      rows = rows.filter(function (allocation) {
        if (!allocation.Created) return false
        var createdDate = new Date(allocation.Created)
        var createdKey = createdDate.getFullYear() + '-' + createdDate.getMonth() + '-' + createdDate.getDate()
        return createdKey === filterKey
      })
    }

    if (state.filters.amountMin !== null) {
      rows = rows.filter(function (allocation) {
        return toAbsoluteNumber(allocation.Amount) >= Number(state.filters.amountMin)
      })
    }
    if (state.filters.amountMax !== null) {
      rows = rows.filter(function (allocation) {
        return toAbsoluteNumber(allocation.Amount) <= Number(state.filters.amountMax)
      })
    }
    if (state.filters.remainingMin !== null) {
      rows = rows.filter(function (allocation) {
        return allocation.Outstanding !== null && allocation.Outstanding !== undefined && Number(allocation.Outstanding) >= Number(state.filters.remainingMin)
      })
    }
    if (state.filters.remainingMax !== null) {
      rows = rows.filter(function (allocation) {
        return allocation.Outstanding !== null && allocation.Outstanding !== undefined && Number(allocation.Outstanding) <= Number(state.filters.remainingMax)
      })
    }
    return rows.map(function (allocation) {
      return Object.assign({}, allocation, { Amount: toAbsoluteNumber(allocation.Amount) })
    })
  }
}

export default {
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters
}
